import { LegacyCardContent } from "../../../../components/card/LegacyCard";
import { ReactElement, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import type { StockListResponseType } from "@sellar-io/api-v2/types";
import { BestPricePromiseDialog } from "../../../../components/bestPricePromise/BestPricePromiseDialog";
import { useEvents } from "../../../../hooks";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import {
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "../../../discovery/v2/DiscoveryV2RecentlyViewedStore";
import { useStorefrontFilterStore } from "../../StorefrontFilterStore";
import { useStockListContext } from "../../useStockListContext";
import { StorefrontTableCategoryDialogContainer } from "../Storefront/table/Dialog/StorefrontTableCategoryDialogContainer";
import { StorefrontTable } from "../Storefront/table/StorefrontTable";
import { StorefrontTableProductRowContainer } from "../Storefront/table/StorefrontTableProductRowContainer";
import CategoryListElementHeader from "./CategoryListElementHeader";
import CategoryListElementMobileContainer from "./CategoryListElementMobileContainer";
import { findLatestPreOrderAvailableFromDate } from "@sellar-io/common";

export default function CategoryListElementContainer({
  category,
}: {
  category: StockListResponseType[number];
}): ReactElement {
  const { sessionId } = useMarketStorefrontSessionId();
  const { business } = useSellarCurrentBusiness();
  const { track } = useEvents();
  const { editing, supplierStorefront } = useStockListContext();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { searchValue, selectedTags } = useStorefrontFilterStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      searchValue: state.searchValue,
    })),
  );
  const { supplierQuery } = useStockListContext();
  const [categoryDialogActive, setCategoryDialogActive] = useState(false);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const latestPreOrderAvailableFromDate = findLatestPreOrderAvailableFromDate(
    category.Products.map((product) => ({
      availableFrom: product.availableFrom,
    })),
  );

  const categoryContainsPreOrderProduct = !!latestPreOrderAvailableFromDate;

  const navigateToItemPage = () => {
    track("Product Category Clicked", {
      categoryId: category.id,
      categoryName: category.name,
      categoryType: category.kind,
      search: searchValue,
      filters: selectedTags,
      supplier: supplierQuery.data?.urlId,
      containsPreOrderProductItem: categoryContainsPreOrderProduct,
      sessionId,
    });
    if (supplierQuery.data?.id)
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: category.id,
        supplierId: supplierQuery.data.id,
        name: category.name?.split(" • ")[0] ?? "",
        imageUrl: category.imageUrl,
        logoUrl: supplierQuery.data.Business.logoUrl,
        urlId: supplierQuery.data.urlId,
        details: `${category.kind} // ${category.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: categoryContainsPreOrderProduct,
      });
    setCategoryDialogActive(true);
  };

  return (
    <>
      <CategoryListElementHeader
        category={category}
        storefrontAvailability={
          supplierQuery.data?.StorefrontAvailability ?? null
        }
        onClick={() => {
          navigateToItemPage();
        }}
      />
      {!editing && (
        <LegacyCardContent className={"md:p-0"}>
          <>
            {isMobile ? (
              <CategoryListElementMobileContainer
                category={category}
                hasDefaultPriceTier={!!supplierStorefront}
              />
            ) : (
              <StorefrontTable>
                {category.Products.map((child) => (
                  <StorefrontTableProductRowContainer
                    key={child.id}
                    item={category}
                    child={child}
                    hasDefaultPriceTier={!!supplierStorefront}
                    categoryContainsPreOrderProduct={
                      categoryContainsPreOrderProduct
                    }
                  />
                ))}
              </StorefrontTable>
            )}
          </>
        </LegacyCardContent>
      )}
      <StorefrontTableCategoryDialogContainer
        productId={category.id}
        active={categoryDialogActive}
        setBestPricePromiseDialogActive={() => {
          setCategoryDialogActive(false);
          setBestPricePromiseDialogActive(true);
        }}
        onDismiss={() => {
          setCategoryDialogActive(false);
        }}
      />
      <BestPricePromiseDialog
        active={bestPricePromiseDialogActive}
        onDismiss={() => setBestPricePromiseDialogActive(false)}
        buyerName={business?.name}
        supplierName={supplierQuery.data?.Business.name}
        productName={category.name}
      />
    </>
  );
}
